type ReferenceItem = {
  id: number
  title?: string
  journal?: string
  volume?: string
  issue?: string
  year?: string
  nameDOI?: string
  linkDOI?: string
  _links: {
    self: {
      href: string
    }
  }
}

/**
 * Extracts the journal, volume, issue, and year from the input string.
 * Prioritizes journal name and year, with volume and issue being optional.
 * @param {string} part - The input string (e.g., "Hypertens Res2024" or "The Lancet 2024").
 * @returns {Partial<ReferenceItem>} - Partial object with journal, volume, issue, and year.
 */
function parseJournalVolumeYear(part: string): Partial<ReferenceItem> {
  const result: Partial<ReferenceItem> = {};

  // Regex to match journal, volume, issue, and year
  const match = part.match(/^(.*?)(?:\s+(\d+))?(?:\s+([\d–-]+))?\s*(\d{4})$/);

  if (match) {
    const potentialJournal = match[1]?.trim(); // Journal name
    const potentialVolume = match[2]?.trim(); // Volume
    const potentialIssue = match[3]?.trim(); // Issue
    const potentialYear = match[4]?.trim(); // Year

    // Assign journal
    if (potentialJournal) {
      result.journal = potentialJournal;
    }

    // Assign volume if present
    if (potentialVolume) {
      result.volume = potentialVolume;
    }

    // Assign issue if present
    if (potentialIssue) {
      result.issue = potentialIssue;
    }

    // Assign year (always present)
    if (potentialYear) {
      result.year = potentialYear;
    }
  }

  return result;
}


/**
 * Extracts the DOI from the input string.
 * @param {string} part - The input string (e.g., "10.1038/s41588-024-01714-w").
 * @returns {Partial<ReferenceItem>} - Partial object with nameDOI and linkDOI.
 */
function parseDOI(part: string): Partial<ReferenceItem> {
  const result: Partial<ReferenceItem> = {}
  const trimmed = part.trim()
  if (trimmed.startsWith('10.')) {
    result.nameDOI = trimmed.split(/\s+/)[0] // Extract DOI (assumes space separates DOI and title)
    result.linkDOI = `https://doi.org/${result.nameDOI}`
  }
  return result
}

/**
 * Extracts the title from the input string, enclosed in «...».
 * @param {string} part - The input string (e.g., "«Genome-wide analysis...»").
 * @returns {Partial<ReferenceItem>} - Partial object with title.
 */
function parseTitle(part: string): Partial<ReferenceItem> {
  const result: Partial<ReferenceItem> = {}
  const match = part.match(/^«(.*)»$/) // Match title enclosed in «...»
  if (match) {
    result.title = match[1].trim()
  } else if (part.trim()) {
    result.title = part.trim()
  }
  return result
}

export function parseReferenceString(str: string): Partial<ReferenceItem> {
  // Initialize default structure
  const reference: Partial<ReferenceItem> = {}

  // Split into parts using flexible detection for "DOI:" or DOI format
  const [firstPart, secondPart] = splitReferenceParts(str)

  // Parse the first part
  if (firstPart) {
    Object.assign(reference, parseFirstPart(firstPart, Boolean(secondPart)))
  }

  // Parse the second part
  if (secondPart) {
    Object.assign(reference, parseSecondPart(secondPart))
  }

  return reference
}

/**
 * Splits the input string into first and second parts.
 * Detects DOI based on "DOI:" or its standard format (10.xxx/...).
 * @param str - The reference string to split.
 * @returns [string, string | undefined] - The first and second parts of the input string.
 */
function splitReferenceParts(str: string): [string, string | undefined] {
  // Regex to find a DOI (e.g., "DOI: 10.1234/..." or "10.1234/...")
  const doiRegex = /,\s*(?:DOI:\s*)?(10\.\S+)/

  const match = str.match(doiRegex)
  if (match) {
    // Split on the matched DOI location
    const parts = str.split(doiRegex)
    return [parts[0].trim(), `${match[1]} ${parts.slice(2).join(' ').trim()}`]
  }

  // If no DOI is found, split on the first comma
  const parts = str.split(/,\s*/, 2)
  return [parts[0], parts[1] || undefined]
}

/**
 * Parses the first part of the reference string.
 * @param firstPart - The first part of the input string.
 * @param hasSecondPart - Indicates if a second part exists.
 * @returns Partial<ReferenceItem>
 */
function parseFirstPart(
  firstPart: string,
  hasSecondPart: boolean
): Partial<ReferenceItem> {
  if (hasSecondPart) {
    // If two parts exist, treat the first part as journal-related
    return parseJournalVolumeYear(firstPart)
  }

  if (firstPart.trim().startsWith('10.')) {
    // If it starts with DOI, parse DOI and remaining part as Title
    const doiInfo = parseDOI(firstPart)
    const remainingTitle = firstPart.replace(doiInfo.nameDOI || '', '').trim()
    return {
      ...doiInfo,
      ...parseTitle(remainingTitle),
    }
  }

  // If no DOI, try parsing as Title or Journal
  const titleInfo = parseTitle(firstPart)
  return titleInfo.title ? titleInfo : parseJournalVolumeYear(firstPart)
}

/**
 * Parses the second part of the reference string.
 * @param secondPart - The second part of the input string.
 * @returns Partial<ReferenceItem>
 */
function parseSecondPart(secondPart: string): Partial<ReferenceItem> {
  // Parse DOI and Title from the second part
  const doiInfo = parseDOI(secondPart)
  const remainingTitle = secondPart.replace(doiInfo.nameDOI || '', '').trim()
  return {
    ...doiInfo,
    ...parseTitle(remainingTitle),
  }
}

export /**
 * Converts a ReferenceItem object back to a free-text reference string.
 * Handles cases where some fields (e.g., journal, title, DOI) might be missing.
 * @param {ReferenceItem} ref - The ReferenceItem object.
 * @returns {string} - Serialized string in the format:
 *                     "{journal}{volume}{issue}{year}, DOI: {nameDOI}, «{title}»"
 */
function referenceItemToString(ref: ReferenceItem): string {
  const parts: string[] = []

  // --- Add Journal, Volume, Issue, and Year ---
  if (ref.journal) parts.push(ref.journal)
  if (ref.volume) parts.push(ref.volume)
  if (ref.issue) parts.push(ref.issue)
  if (ref.year) parts.push(ref.year)

  // Join journal-related parts into one segment
  const journalPart = parts.join(' ')

  // --- Add DOI ---
  const doiPart = ref.nameDOI ? `DOI: ${ref.nameDOI}` : ''

  // --- Add Title ---
  const titlePart = ref.title ? `«${ref.title}»` : ''

  // --- Combine all parts ---
  // Include journalPart only if it's not empty
  const result = [journalPart, doiPart, titlePart].filter(Boolean).join(', ')

  return result.trim()
}
